import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  canScheduleLocalNotifications,
  onAppResume,
  onLocalNotificationTap,
} from '@mlm/ui/lib/native-shell';
import { useProfile } from './queries';
import { syncReminders } from './reminders';

/**
 * Inside the phone app, keep the scheduled reminders in step with the member's
 * saved preferences: on start, whenever they change, and each time the app
 * comes back to the foreground (the phone may have changed timezone, or the OS
 * may have dropped the alarms). Never prompts for permission; the settings
 * screen does that when the member saves. A tapped reminder opens its screen.
 *
 * On the web every branch is a no-op.
 */
export function useReminderSync() {
  const profile = useProfile();
  const navigate = useNavigate();
  const morning = profile.data?.profile.notificationPrefs.morningReminder ?? null;
  const evening = profile.data?.profile.notificationPrefs.eveningReminder ?? null;
  const timeZone = profile.data?.profile.timezone ?? null;
  const loaded = Boolean(profile.data);

  useEffect(() => {
    if (!loaded || !timeZone || !canScheduleLocalNotifications()) return;
    const sync = () =>
      void syncReminders({ morningReminder: morning, eveningReminder: evening }, timeZone, {
        prompt: false,
      });
    sync();
    return onAppResume(sync);
  }, [loaded, morning, evening, timeZone]);

  useEffect(
    () =>
      onLocalNotificationTap((extra) => {
        const path = extra.path;
        // Only in-app paths: a reminder can never send the member anywhere else.
        if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) {
          navigate(path);
        }
      }),
    [navigate],
  );
}
